import { useState, useEffect, useMemo, useContext } from "react";
import {
  DropZone,
  IllustratedMessage,
  Heading,
  Content,
  ButtonGroup,
  Button,
  FileTrigger,
  ProgressCircle,
  Text,
} from "@react-spectrum/s2";
import { useVideo, useVideoTemplate } from "@app/hooks/videos";
import { usePresignURL } from "@app/hooks/s3";
import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import { getMinDuration, getMaxDuration } from "./VoiceoverAsset";
import {
  AudioPlayer,
  getFormattedTime,
  getTrimmedDuration,
} from "@app/components/AudioPlayer";
import { InlineNotice } from "@app/components/InlineNotice";
import CloudUploadIcon from "@react-spectrum/s2/illustrations/linear/CloudUpload";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const ACCEPTED_AUDIO_TYPES = ["audio/wav", "audio/mpeg", "audio/mp3"];

const AUDIO_TYPE_OVERRIDES = {
  "audio/mpeg": "audio/mp3",
};

const normalizeAudioFile = (file) => {
  const overrideType = AUDIO_TYPE_OVERRIDES[file?.type];
  if (!overrideType) return file;
  return new File([file], file.name, {
    type: overrideType,
    lastModified: file.lastModified,
  });
};

export const UPLOAD_MUX_SOURCE_KEY = "upload";
export const UPLOADED_VOICEOVER_ASSET_NAME = "uploaded-voiceover";

export const VoiceoverUpload = ({ videoId, file, onFileChange }) => {
  const video = useVideo(videoId);
  const template = useVideoTemplate(video.data?.templateId);

  const { uploadAssets } = useContext(VideoWizardContext);

  const templateAssets = template.data?.assets?.filter(
    (asset) => asset.type === "audio",
  );

  const audioVariations =
    templateAssets
      ?.map((asset) =>
        template.data?.audioVariations?.find(
          (audioVariation) => audioVariation.referenceKey === asset.name,
        ),
      )
      ?.filter(Boolean) ?? [];

  const maxDuration = audioVariations.reduce((max, asset) => {
    return (asset?.settings?.inset ?? 0) + (asset?.settings?.duration ?? 0) >
      max
      ? (asset?.settings?.inset ?? 0) + (asset?.settings?.duration ?? 0)
      : max;
  }, 0);

  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const audioUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : null),
    [file],
  );

  useEffect(() => {
    if (!audioUrl) return;
    return () => URL.revokeObjectURL(audioUrl);
  }, [audioUrl]);

  useEffect(() => {
    if (!file) setError(null);
  }, [file]);

  const uploadedAsset = video.data?.assets?.find(
    (a) => a.name === UPLOADED_VOICEOVER_ASSET_NAME,
  );
  const uploadedAssetPresign = usePresignURL(uploadedAsset?.filePath);

  const hasUpload = !!file || !!uploadedAsset;
  const playerUrl = file ? audioUrl : uploadedAssetPresign.data?.presignURL;
  const isLoadingExisting =
    !file && !!uploadedAsset && !uploadedAssetPresign.data?.presignURL;

  const handleAudioReady = (waveform) => {
    const duration = getTrimmedDuration(waveform);

    if (duration > getMaxDuration(maxDuration)) {
      setError([
        `Audio is too long. Please upload an audio that is about ${getFormattedTime(maxDuration)} minutes long.`,
      ]);
    } else if (duration < getMinDuration(maxDuration)) {
      setError([
        `Audio is too short. Please upload an audio that is about ${getFormattedTime(maxDuration)} minutes long.`,
      ]);
    } else {
      setError(null);
    }
  };

  const handleFile = async (selectedFile) => {
    onFileChange?.(selectedFile);

    if (!selectedFile) return;

    setError(null);
    setIsUploading(true);
    try {
      await uploadAssets.mutateAsync({
        assets: [
          {
            name: UPLOADED_VOICEOVER_ASSET_NAME,
            type: "audio",
            blob: normalizeAudioFile(selectedFile),
            generatedAt: Date.now(),
          },
        ],
      });
    } catch (e) {
      setError(["Failed to upload audio. Please try again."]);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = async (event) => {
    let item = event.items.find(
      (item) =>
        item.kind === "file" && ACCEPTED_AUDIO_TYPES.includes(item.type),
    );

    if (item?.kind === "file") {
      let file = await item.getFile();
      handleFile(file);
    }
  };

  const handleFileSelect = (files) => {
    if (!files) return;
    handleFile(files[0]);
  };

  return (
    <>
      <DropZone
        styles={style({ maxWidth: 1000, width: "full", marginX: "auto" })}
        isFilled={hasUpload}
        getDropOperation={(types) =>
          ACCEPTED_AUDIO_TYPES.some((t) => types.has(t)) ? "copy" : "cancel"
        }
        onDrop={handleDrop}
      >
        {hasUpload ? (
          isUploading || isLoadingExisting || !playerUrl ? (
            <div
              className={style({
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                paddingY: 16,
              })}
            >
              <ProgressCircle
                aria-label={isUploading ? "Uploading audio" : "Loading audio"}
                isIndeterminate
                size="S"
              />
              <Text>{isUploading ? "Uploading audio…" : "Loading audio…"}</Text>
            </div>
          ) : (
            <AudioPlayer url={playerUrl} onReady={handleAudioReady} />
          )
        ) : (
          <IllustratedMessage>
            <CloudUploadIcon />
            <Heading>Drag and drop your file</Heading>
            <Content>Or, select a file from your computer</Content>
            <ButtonGroup>
              <FileTrigger
                acceptedFileTypes={ACCEPTED_AUDIO_TYPES}
                onSelect={handleFileSelect}
              >
                <Button variant="accent">Browse files</Button>
              </FileTrigger>
            </ButtonGroup>
          </IllustratedMessage>
        )}
      </DropZone>
      {error && <InlineNotice errorMessage={error} />}
    </>
  );
};
